import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../apiClient';
import PlannerToolbar from './PlannerToolbar';
import { TabBar } from './registerTabs';
import { TODAY_START } from './plannerState';
import {
  monthIdx, SCENARIOS, bucketize, buildUtil, isScenarioView, serializeScenario, totals, utilBand, utilPct,
} from './utilizationLogic';

const TABS = ['Overview', 'By Resource', 'By Project', 'What-If'];
const CHART_MAX = 120; // bar scale tops out at 120% so over-100 still shows
// Marker on red and over-100 cells so the bands survive colorblindness (Judy's optional suggestion).
const MARK = { low: '▼', over: '▲' };

const Pct = ({ demand, capacity, title }) => {
  const band = utilBand(demand, capacity);
  const pct = utilPct(demand, capacity);
  return (
    <td className={`up-cell up-${band}`} title={title || `${Math.round(demand)} h of ${Math.round(capacity)} h`}>
      {band === 'none' ? '' : `${pct == null ? '—' : `${pct}%`}${MARK[band] ? ` ${MARK[band]}` : ''}`}
    </td>
  );
};

const Legend = () => (
  <div className="up-legend">
    <span className="up-cell up-good">65%+</span><span className="up-cell up-warn">50–64%</span>
    <span className="up-cell up-low">Under 50% ▼</span><span className="up-cell up-over">Over 100% ▲</span>
    <span className="text-muted">Utilization = assigned hours ÷ the person's own capacity.</span>
  </div>
);

export default function UtilizationPlanner({ shared }) {
  const [feed, setFeed] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('Overview');
  const {
    grain, setGrain, teamIds, setTeamIds, includePipeline, setIncludePipeline, start, setStart,
    scenarios, setScenarios, savedId, setSavedId,
  } = shared;
  const WINDOW = shared.windowMonths;
  const [saved, setSaved] = useState([]);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const [msg, setMsg] = useState('');
  const [openTeams, setOpenTeams] = useState(() => new Set());
  const [openProjects, setOpenProjects] = useState(() => new Set());

  const loadSaved = useCallback(
    () => apiFetch('/capacity-planner/views').then((vs) => setSaved(vs.filter((v) => isScenarioView(v.config)))).catch(() => setSaved([])),
    [],
  );
  useEffect(() => {
    apiFetch('/capacity-planner').then(setFeed).catch((e) => setError(e.message || 'Could not load the planner.'));
    loadSaved();
  }, [loadSaved]);

  const today = new Date();
  const nowIdx = today.getFullYear() * 12 + today.getMonth();

  // A 24-month slice of the feed; scenarios are applied to the full demand first, then the slice cuts.
  const win = useMemo(() => {
    if (!feed) return null;
    const s = Math.max(0, Math.min(start, feed.months.length - WINDOW));
    return { ...feed, months: feed.months.slice(s, s + WINDOW), people: feed.people.map((p) => ({ ...p, capacity: p.capacity.slice(s, s + WINDOW) })) };
  }, [feed, start, WINDOW]);

  const grid = useMemo(() => win && buildUtil(win, { teamIds, includePipeline, scenarios, nowIdx }), [win, teamIds, includePipeline, scenarios, nowIdx]);
  const baseGrid = useMemo(() => win && buildUtil(win, { teamIds, includePipeline, nowIdx }), [win, teamIds, includePipeline, nowIdx]);

  if (error) return <div className="card"><p className="text-muted">{error}</p></div>;
  if (!feed || !win) return <div className="card"><p className="text-muted">Loading…</p></div>;

  const changed = Object.values(scenarios).some((s) => s !== 'plan');
  const teamOptions = feed.teams.map((t) => ({ value: t.id, label: t.name }));
  const pickSaved = (id) => shared.pickScenario(id, saved);
  const saveScenario = async () => {
    try {
      const created = await apiFetch('/capacity-planner/views', { method: 'POST', body: JSON.stringify({ name: name.trim(), config: serializeScenario(scenarios), isShared: false }) });
      await loadSaved();
      setSavedId(created.id);
      setNaming(false);
      setName('');
      setMsg('');
    } catch (e) {
      setMsg(e.message || 'Could not save the scenario.');
    }
  };
  const deleteSaved = async () => {
    await apiFetch(`/capacity-planner/views/${savedId}`, { method: 'DELETE' }).catch(() => {});
    setSavedId('');
    loadSaved();
  };
  const activeSaved = saved.find((s) => s.id === savedId);

  const buckets = (d, c) => bucketize(win, d, c, grain);
  const head = buckets(grid.teams[0]?.demand || [], grid.teams[0]?.capacity || []).map((b) => b.label);
  const all = totals(grid);
  const yearSpans = [];
  win.months.forEach((m) => { const l = yearSpans[yearSpans.length - 1]; if (l?.year === m.year) l.n += 1; else yearSpans.push({ year: m.year, n: 1 }); });
  const toggle = (setter, id) => setter((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const Chevron = ({ open }) => <span className="up-chev">{open ? '▾' : '▸'}</span>;

  // ---------- Overview ----------
  const k0 = Math.max(0, nowIdx - monthIdx(win.months[0].year, win.months[0].month));
  const sum12 = (a) => a.slice(k0, k0 + 12).reduce((x, y) => x + y, 0);
  const [d12, c12] = [sum12(all.demand), sum12(all.capacity)];
  const people = grid.teams.flatMap((t) => t.people.map((p) => ({ ...p, team: t.team })));
  const avg6 = (p) => {
    const d = p.demand.slice(k0, k0 + 6).reduce((a, b) => a + b, 0);
    const c = p.capacity.slice(k0, k0 + 6).reduce((a, b) => a + b, 0);
    return { ...p, pct: utilPct(d, c), d, c };
  };
  const attention = people.map(avg6).filter((p) => p.c > 0 && p.pct != null && (p.pct < 50 || p.pct > 100)).sort((a, b) => Math.abs(b.pct - 75) - Math.abs(a.pct - 75));
  const unassignedHours = grid.teams.reduce((a, t) => a + t.unassigned.slice(k0, k0 + 12).reduce((x, y) => x + y, 0), 0);
  const alerts = [];
  for (const t of grid.teams) {
    const bs = buckets(t.demand, t.capacity);
    const first = bs.find((b) => b.capacity > 0 && utilBand(b.demand, b.capacity) === 'low');
    const over = bs.find((b) => utilBand(b.demand, b.capacity) === 'over');
    if (first) alerts.push(`${t.team.name} falls to ${utilPct(first.demand, first.capacity)}% in ${first.label}.`);
    if (over) alerts.push(`${t.team.name} is over capacity in ${over.label} (${utilPct(over.demand, over.capacity)}%).`);
  }

  const overview = (
    <>
      <div className="up-kpis">
        <div className="up-kpi"><div className="up-kpi-n">{utilPct(d12, c12) ?? '—'}%</div>Next 12 months, all teams</div>
        <div className="up-kpi"><div className="up-kpi-n">{attention.filter((p) => p.pct < 50).length}</div>People under 50% (next 6 mo)</div>
        <div className="up-kpi"><div className="up-kpi-n">{attention.filter((p) => p.pct > 100).length}</div>People over 100% (next 6 mo)</div>
        <div className="up-kpi"><div className="up-kpi-n">{Math.round(unassignedHours).toLocaleString()}</div>Team-level hours not yet named</div>
      </div>
      <div className="up-split">
        <div className="card">
          <div className="card-title">Total utilization by month <span className="text-muted">all teams, % of capacity</span></div>
          <div className="up-chart">
            {[100, 65, 50].map((g) => <div key={g} className="up-guide" style={{ bottom: `${(g / CHART_MAX) * 100}%` }}><span>{g}%</span></div>)}
            {win.months.map((m, k) => {
              const p = utilPct(all.demand[k], all.capacity[k]);
              return (
                <div key={k} className="up-bar-col" title={`${m.year}-${String(m.month).padStart(2, '0')}: ${p ?? '—'}%`}>
                  <div className={`up-bar up-${utilBand(all.demand[k], all.capacity[k])}`} style={{ height: `${(Math.min(CHART_MAX, p || 0) / CHART_MAX) * 100}%` }}><b>{p == null ? '' : `${p}%`}</b></div>
                </div>
              );
            })}
          </div>
          <div className="up-axis">{win.months.map((m, k) => <span key={k}>{'JFMAMJJASOND'[m.month - 1]}</span>)}</div>
          <div className="up-years">{yearSpans.map((y) => <span key={y.year} style={{ flex: y.n }}>{y.year}</span>)}</div>
        </div>
        <div className="card">
          <div className="card-title">Teams by {grain} <span className="text-muted">average utilization</span></div>
          <div className="up-scroll">
            <table className="up-table">
              <thead><tr><th>Team</th>{head.map((h) => <th key={h}>{h}</th>)}</tr></thead>
              <tbody>
                {grid.teams.map((t) => (
                  <tr key={t.team.id}><th>{t.team.name}</th>{buckets(t.demand, t.capacity).map((b) => <Pct key={b.key} demand={b.demand} capacity={b.capacity} />)}</tr>
                ))}
                <tr className="up-team"><th>All teams</th>{buckets(all.demand, all.capacity).map((b) => <Pct key={b.key} demand={b.demand} capacity={b.capacity} />)}</tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="up-two">
        <div className="card">
          <div className="card-title">Who needs attention</div>
          {attention.length === 0 ? <p className="text-muted">Nobody is outside the bands over the next six months.</p> : (
            <ul className="up-list">
              {attention.slice(0, 16).map((p) => <li key={p.person.id}><span className={`up-cell up-${utilBand(p.d, p.c)}`}>{p.pct}%</span> {p.person.name} <span className="text-muted">· {p.team.name}</span></li>)}
            </ul>
          )}
        </div>
        <div className="card">
          <div className="card-title">Horizon alerts</div>
          {alerts.length === 0 ? <p className="text-muted">No team drops below 50% or goes over 100%.</p> : <ul className="up-list">{alerts.map((a) => <li key={a}>{a}</li>)}</ul>}
        </div>
      </div>
    </>
  );

  // ---------- By Resource ----------
  const byEmployee = (
    <div className="card">
      <div className="up-actions up-pad">
        <button className="btn btn-outline" onClick={() => setOpenTeams(new Set(grid.teams.map((t) => t.team.id)))}>Expand all</button>
        <button className="btn btn-outline" onClick={() => setOpenTeams(new Set())}>Collapse all</button>
      </div>
      <div className="up-scroll">
        <table className="up-table">
          <thead><tr><th>Resource</th>{head.map((h) => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>
            {grid.teams.map((t) => {
              const open = openTeams.has(t.team.id);
              return (
                <React.Fragment key={t.team.id}>
                  <tr className="up-team">
                    <th><button className="up-toggle" aria-expanded={open} aria-label={`${open ? 'Collapse' : 'Expand'} ${t.team.name}`} onClick={() => toggle(setOpenTeams, t.team.id)}><Chevron open={open} /> {t.team.name}</button></th>
                    {buckets(t.demand, t.capacity).map((b) => <Pct key={b.key} demand={b.demand} capacity={b.capacity} />)}
                  </tr>
                  {open && t.people.filter((p) => p.capacity.some(Boolean)).map((p) => (
                    <tr key={p.person.id}><th className="up-indent">{p.person.name}</th>{buckets(p.demand, p.capacity).map((b) => <Pct key={b.key} demand={b.demand} capacity={b.capacity} />)}</tr>
                  ))}
                  {open && t.unassigned.some(Boolean) && (
                    <tr className="up-unassigned">
                      <th className="up-indent" title="Forecast entered for the team, not yet named to a person">Not yet assigned</th>
                      {buckets(t.unassigned, t.capacity).map((b) => <td key={b.key} className="up-cell up-un" title={`${Math.round(b.demand)} h`}>{b.demand ? `${Math.round(b.demand)} h` : ''}</td>)}
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-muted up-note">Teams count every forecast hour. Resources show only hours named to them; “Not yet assigned” is the team-level forecast waiting for names.</p>
    </div>
  );

  // ---------- By Project ----------
  // A project's cell is its hours as a share of the team's capacity, so a row reads "how much of this
  // team does this project eat". Shaded, not banded: 20% on one project is not a "red" anything.
  const nameOf = new Map(feed.projects.map((p) => [p.id, p.name]));
  const projectRows = [...new Set(grid.teams.flatMap((t) => [...t.byProject.keys()]))]
    .sort((a, b) => (nameOf.get(a) || '').localeCompare(nameOf.get(b) || ''));
  const Share = ({ hours, capacity }) => {
    const pct = capacity > 0 ? Math.round((hours / capacity) * 100) : null;
    return <td className="up-cell up-share" style={hours > 0 ? { background: `rgba(30, 90, 160, ${Math.min(0.55, (pct || 0) / 100 * 1.2 + 0.08)})` } : undefined} title={`${Math.round(hours)} h of ${Math.round(capacity)} h team capacity`}>{hours > 0 ? (pct == null ? '—' : `${pct}%`) : ''}</td>;
  };
  const sumArr = (arrs) => (arrs.length ? arrs[0].map((_, k) => arrs.reduce((a, x) => a + x[k], 0)) : []);
  const byProject = (
    <div className="card">
      <div className="up-actions up-pad">
        <button className="btn btn-outline" onClick={() => setOpenProjects(new Set(projectRows))}>Expand all</button>
        <button className="btn btn-outline" onClick={() => setOpenProjects(new Set())}>Collapse all</button>
      </div>
      <div className="up-scroll">
        <table className="up-table">
          <thead><tr><th>Project</th>{head.map((h) => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>
            {projectRows.map((pid) => {
              const open = openProjects.has(pid);
              const withTeam = grid.teams.filter((t) => t.byProject.has(pid));
              const hb = buckets(sumArr(withTeam.map((t) => t.byProject.get(pid))), sumArr(withTeam.map((t) => t.capacity)));
              return (
                <React.Fragment key={pid}>
                  <tr className="up-team">
                    <th><button className="up-toggle" aria-expanded={open} aria-label={`${open ? 'Collapse' : 'Expand'} ${nameOf.get(pid)}`} onClick={() => toggle(setOpenProjects, pid)}><Chevron open={open} /> {nameOf.get(pid)}</button></th>
                    {hb.map((b) => <Share key={b.key} hours={b.demand} capacity={b.capacity} />)}
                  </tr>
                  {open && withTeam.map((t) => (
                    <tr key={t.team.id}><th className="up-indent">{t.team.name}</th>{buckets(t.byProject.get(pid), t.capacity).map((b) => <Share key={b.key} hours={b.demand} capacity={b.capacity} />)}</tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-muted up-note">Each cell is the project's hours as a share of the team's capacity (project rows: of the capacity of the teams working on it). Follows the scenario if one is active.</p>
    </div>
  );

  // ---------- What-If ----------
  const live = new Set(feed.demand.filter((d) => { const i = monthIdx(d.year, d.month) - monthIdx(feed.months[0].year, feed.months[0].month); return i >= start && i < start + WINDOW; }).map((d) => d.projectId));
  const projects = feed.projects.filter((p) => live.has(p.id) && (includePipeline || !p.isPipeline));
  const deltas = grid.teams.map((t, i) => {
    const now = buckets(t.demand, t.capacity);
    const was = buckets(baseGrid.teams[i].demand, baseGrid.teams[i].capacity);
    return { team: t.team, rows: now.map((b, j) => ({ label: b.label, now: utilPct(b.demand, b.capacity), was: utilPct(was[j].demand, was[j].capacity) })) };
  });
  const moved = deltas.map((t) => ({ ...t, biggest: t.rows.reduce((m, r) => (r.now != null && r.was != null && Math.abs(r.now - r.was) > Math.abs(m.now - m.was) ? r : m), { now: 0, was: 0 }) })).filter((t) => t.biggest.now !== t.biggest.was);
  const whatIf = (
    <>
      <div className="card">
        <div className="card-title">Change a project, see everything recalculate</div>
        <p className="text-muted">Nothing here is saved to the project; the baseline stays one click away. “Stalls” holds work already behind us and pushes the rest; “Slips” moves the whole project.</p>
        <div className="up-scroll up-projects">
          <table className="up-table">
            <tbody>
              {projects.map((p) => (
                <tr key={p.id}>
                  <th>{p.name}{p.isPipeline ? ' (pipeline)' : ''}</th>
                  <td>
                    <select className="form-select" aria-label={`Scenario for ${p.name}`} value={scenarios[p.id] || 'plan'} onChange={(e) => { setSavedId(''); setScenarios((s) => ({ ...s, [p.id]: e.target.value })); }}>
                      {Object.entries(SCENARIOS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="up-actions">
          <button className="btn btn-outline" disabled={!changed} onClick={() => { setScenarios({}); setSavedId(''); }}>Reset to baseline</button>
          {naming ? (
            <>
              <input className="form-input" placeholder="Scenario name" value={name} onChange={(e) => setName(e.target.value)} />
              <button className="btn btn-primary" disabled={!name.trim()} onClick={saveScenario}>Save</button>
              <button className="btn btn-outline" onClick={() => setNaming(false)}>Cancel</button>
            </>
          ) : <button className="btn btn-outline" disabled={!changed} onClick={() => setNaming(true)}>Save scenario…</button>}
          {msg && <span className="text-muted">{msg}</span>}
        </div>
      </div>
      <div className="card">
        <div className="card-title">Baseline vs. scenario, by team</div>
        {!changed ? <p className="text-muted">Pick a different timing for a project above.</p> : (
          <>
            <div className="up-scroll">
              <table className="up-table">
                <thead><tr><th>Team</th>{head.map((h) => <th key={h}>{h}</th>)}</tr></thead>
                <tbody>
                  {deltas.map((t) => (
                    <tr key={t.team.id}>
                      <th>{t.team.name}</th>
                      {t.rows.map((r) => {
                        const cap = grid.teams.find((x) => x.team.id === t.team.id);
                        const b = buckets(cap.demand, cap.capacity)[t.rows.indexOf(r)];
                        return <Pct key={r.label} demand={b.demand} capacity={b.capacity} title={`${r.was ?? '—'}% → ${r.now ?? '—'}%`} />;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-muted up-note">Hover a cell for baseline → scenario. {moved.length ? `Biggest movers: ${moved.map((t) => `${t.team.name} ${t.biggest.was}% → ${t.biggest.now}% (${t.biggest.label})`).join('; ')}.` : ''}</p>
          </>
        )}
      </div>
    </>
  );

  return (
    <div className="up-page">
      <PlannerToolbar
        teamOptions={teamOptions} teamIds={teamIds} onTeams={setTeamIds}
        scenarioId={savedId} scenarioLabel={changed ? 'Unsaved scenario' : 'Baseline'} saved={saved} onScenario={pickSaved}
        grain={grain} onGrain={setGrain} includePipeline={includePipeline} onPipeline={setIncludePipeline}
        windowMonths={WINDOW} onWindow={shared.setWindowMonths}
        onEarlier={shared.earlier} onToday={() => setStart(TODAY_START)}
        onLater={() => shared.later(feed.months.length)}
      >
        {activeSaved?.mine && <button className="btn btn-outline" onClick={deleteSaved}>Delete scenario</button>}
      </PlannerToolbar>
      <TabBar tabs={TABS.map((t) => ({ key: t, label: t }))} active={tab} onSelect={setTab} />
      {changed && tab !== 'What-If' && <div className="up-banner">Showing a what-if scenario, not the baseline.</div>}
      <Legend />
      {tab === 'Overview' && overview}
      {tab === 'By Resource' && byEmployee}
      {tab === 'By Project' && byProject}
      {tab === 'What-If' && whatIf}
    </div>
  );
}
